import React, { useState } from "react";
import styled from "styled-components";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import { COLOR_SYSTEM } from "components/design/design-system";

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const InfoIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const InfoTooltip = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: ${COLOR_SYSTEM.get("Gray")?.[800]};
  color: ${COLOR_SYSTEM.get("Gray")?.[0]};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  visibility: ${({ $show }) => ($show ? "visible" : "hidden")};
  transition: opacity 0.2s, visibility 0.2s;

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 10px;
    border: 4px solid transparent;
    border-bottom-color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  }
`;

interface SectionHeaderProps {
  title: string;
  description?: string;
  rightSlot?: React.ReactNode;
}

/**
 * 대시보드 섹션 카드의 공통 헤더.
 * 헤더 폰트: H3 (20px/700). 설명은 제목 옆 ⓘ 아이콘 호버 시 툴팁으로 표시.
 */
const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  description,
  rightSlot,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <TitleRow>
      <TitleLeft>
        <Text textType="H3" color={COLOR_SYSTEM.get("Gray")?.[800]}>
          {title}
        </Text>
        {description && (
          <InfoWrapper>
            <InfoIconButton
              type="button"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip((s) => !s)}
              aria-label="설명 보기"
            >
              <Icon
                name="Information Circle"
                size={16}
                color={COLOR_SYSTEM.get("Gray")?.[400]}
              />
            </InfoIconButton>
            <InfoTooltip $show={showTooltip}>{description}</InfoTooltip>
          </InfoWrapper>
        )}
      </TitleLeft>
      {rightSlot}
    </TitleRow>
  );
};

export default SectionHeader;
